import type { CardOrientation, typeCard } from '@susu/custom-made/components/card/card';
import { Card, CardInfoIconType, CardSize } from '@susu/custom-made/components/card/card';
import { useMissingValues } from '@susu/custom-made/hooks/useMissingValues';
import { useViewSize, ViewSize } from '@susu/custom-made/hooks/useViewSize';
import { useEnvironmentContext } from '@susu/custom-made/providers/hooks/useEnvironmentContext';
import { isOptionHiddenByRule, isOptionValueHiddenByRule } from '@susu/custom-made/utils/businessRules';
import { fabricThumbnailDefaultPath, imageDefinition } from '@susu/custom-made/utils/imageDefinition';
import { imageUrlBuilder } from '@susu/custom-made/utils/imageUrlProvider/imageUrlProvider';
import type { MonogramPositions, Option, OptionValue, typeOptionValue } from '@susu/custom-made/utils/interfaces';
import type { ConfigurationTypes } from '@susu/custom-made/utils/productDefinition';
import { Clients } from '@susu/custom-made/utils/constants';
import { getImagePaths, getImageUrl, getThumbnails } from '@susu/custom-made/utils/utils';
import cn from 'classnames';
import type { RefObject } from 'react';

import type { cardItemsType } from './cardList';
import useFabricContext from '@susu/custom-made/providers/hooks/useFabricContext';
import useMissingSelectionValuesContext from '@susu/custom-made/providers/hooks/useMissingSelectionValuesContext';
import useOptionContext from '@susu/custom-made/providers/hooks/useOptionContext';
import { useI18N } from '@susu/i18n/client';
import useUserContext from '@susu/custom-made/providers/hooks/useUserContext';
import { formatPrice } from '@susu/custom-made/utils/formatPrice';

const getAdvancedItemClasses = (isAdvanced: boolean, isAdvancedExpanded: boolean) =>
  cn({
    hidden: isAdvanced && !isAdvancedExpanded,
    block: isAdvanced && isAdvancedExpanded,
  });

export default function CardListOption({
  cardItems,
  cardSubItems,
  selectedItemId,
  cardOrientation,
  isGroup,
  isAdvancedExpanded,
  optionValues,
  isPresentationOnly,
  isDetailedView,
  cardClick = () => {},
  selectedOption,
  cardItemRef,
  affectedConfigurationTypeId,
  isGrid = false,
}: Readonly<{
  cardItems: cardItemsType;
  cardSubItems?: (string | number)[];
  selectedItemId?: number | null;
  cardOrientation?: CardOrientation;
  isGroup?: boolean;
  isAdvancedExpanded: boolean;
  optionValues?: typeOptionValue;
  isPresentationOnly?: boolean;
  isDetailedView?: boolean;
  cardClick?: (card: typeCard) => void;
  selectedOption?: Option | null;
  cardItemRef?: RefObject<HTMLDivElement>;
  affectedConfigurationTypeId?: ConfigurationTypes;
  isGrid?: boolean;
}>) {
  const { t } = useI18N();
  const viewSize = useViewSize();
  const { isOptionWithoutSelection } = useMissingValues();
  const { selectedFabric } = useFabricContext();
  const { monogramsIdsWithMissingSelection } = useMissingSelectionValuesContext();
  const { selectedOptions, hiddenOptionsAndValues, selectedMonogram } = useOptionContext();
  const { cdnDomain } = useEnvironmentContext();
  const { client } = useUserContext();
  const isInStore = client === Clients.inStore;

  const isOptionWithMissingSelection = (item: Option) => {
    let isOptionWithMissingSelection: boolean = false;

    if (item.type === 'OPTION' && isOptionWithoutSelection(item, affectedConfigurationTypeId)) {
      isOptionWithMissingSelection = true;
    }

    if (item.type === 'OPTION_GROUP' && item.options) {
      isOptionWithMissingSelection = item.options.some(option =>
        isOptionWithoutSelection(option, affectedConfigurationTypeId),
      );
    }

    if (item.type === 'MONOGRAM' && monogramsIdsWithMissingSelection) {
      isOptionWithMissingSelection = monogramsIdsWithMissingSelection.length > 0;
    }

    return isOptionWithMissingSelection;
  };

  const getSelectedOption = (item: Option) => {
    const selectedOption = item.options
      ? (affectedConfigurationTypeId && selectedOptions[affectedConfigurationTypeId][item.options[0].id]) ||
        item.options[0].optionValues![0]
      : (affectedConfigurationTypeId && selectedOptions[affectedConfigurationTypeId][item.id]) || item.optionValues![0];

    return {
      name: optionValues![selectedOption].name,
      selectedOption: selectedOption,
    };
  };

  const getOptionCardData = (item: Option, selected?: ReturnType<typeof getSelectedOption>) => {
    let subText = '';
    let imageThumbnail;
    let fallbackImageThumbnail;
    if (item.type === 'MONOGRAM') {
      const imagePath = getImageUrl(item.imageUrlTemplate, item.imageName, selectedFabric?.layerImageName);
      if (!Object.keys(selectedMonogram).length) {
        subText = t('cardListOption.none');
      } else {
        subText = Object.values(selectedMonogram)[0].initials;
      }

      const thumnailData = getThumbnails(imagePath, cdnDomain, true);
      imageThumbnail = thumnailData.imageThumbnail;
      fallbackImageThumbnail = thumnailData.fallbackImageThumbnail;
    } else {
      const { name, selectedOption } = selected ?? getSelectedOption(item);
      subText = name;

      if (
        optionValues![selectedOption].imageUrlTemplate === '{fabricThumbnailDefaultPath}' &&
        optionValues![selectedOption].imageName === '{fabricImageName}'
      ) {
        imageThumbnail = imageUrlBuilder({
          imageUrls: [`${fabricThumbnailDefaultPath}${selectedFabric?.imageName}`],
          imageDefinition: imageDefinition.thumbnail,
          cdnDomain,
        }).src;
        fallbackImageThumbnail = imageThumbnail;
      } else {
        const imagePath = getImageUrl(
          optionValues![selectedOption].imageUrlTemplate,
          optionValues![selectedOption].imageName,
          selectedFabric?.layerImageName,
        );

        const thumnailData = getThumbnails(imagePath, cdnDomain, true);
        imageThumbnail = thumnailData.imageThumbnail;
        fallbackImageThumbnail = thumnailData.fallbackImageThumbnail;
      }
    }

    return {
      subText,
      imageUrl: imageThumbnail,
      fallbackImageUrl: fallbackImageThumbnail,
    };
  };

  const getSubText = (selectedOption: Option | undefined | null, item: OptionValue) => {
    if (selectedOption?.showDescription) {
      return item.description;
    } else if (selectedOption?.appendCode) {
      return item.code;
    } else {
      return undefined;
    }
  };

  const getSelectedMonograms = (item: Option) => {
    if (!item.positions) {
      return [];
    }
    const monogramList: MonogramPositions[] = [];
    Object.values(item.positions).forEach(position => {
      if (selectedMonogram[position.id]) {
        monogramList.push(position);
      }
    });

    return monogramList;
  };

  return (
    <>
      {isGroup &&
        cardItems &&
        (cardItems as Option[]).map((cardItem: Option, index: number) => {
          const item = cardItem;
          if (item && !isOptionHiddenByRule(item.id, hiddenOptionsAndValues, affectedConfigurationTypeId)) {
            const foundPositions: MonogramPositions[] = getSelectedMonograms(item);

            if (item.type === 'MONOGRAM' && isPresentationOnly && isDetailedView && foundPositions.length) {
              return foundPositions.map(position => {
                const subText =
                  selectedMonogram[position.id].initials +
                  ' • ' +
                  item.colors![selectedMonogram[position.id].colorId!].name +
                  ' • ' +
                  item.fontTypes![selectedMonogram[position.id].fontStyleId!].name;

                const isMissing = isOptionWithMissingSelection(item);
                const imageUrls = getImageUrl(
                  position.imageUrlTemplate,
                  position.imageName,
                  selectedFabric?.layerImageName || '',
                );

                return (
                  <div
                    key={index + '_' + position.id}
                    className={getAdvancedItemClasses(item.isAdvanced, isAdvancedExpanded)}
                  >
                    <Card
                      card={item}
                      cardImages={[
                        imageUrlBuilder({
                          imageUrls: [imageUrls.imageUrl],
                          imageDefinition: imageDefinition.thumbnail,
                          noTransformation: true,
                          cdnDomain,
                        }).src,
                      ]}
                      cardText={position.name}
                      isSelected={false}
                      tabIndex={index + 1}
                      cardClick={cardClick}
                      hideMobileInfoText={false}
                      cardSize={CardSize.Medium}
                      isParent={true}
                      cardOrientation={cardOrientation}
                      cardSubText={subText}
                      isPresentationOnly={isPresentationOnly}
                      showWarning={isMissing}
                      cardFallbackImageUrl={
                        imageUrlBuilder({
                          imageUrls: [imageUrls.fallbackUrl],
                          imageDefinition: imageDefinition.thumbnail,
                          noTransformation: true,
                          cdnDomain,
                        }).src
                      }
                      isGrid={isGrid}
                    />
                  </div>
                );
              });
            } else {
              const selected = item.type !== 'MONOGRAM' ? getSelectedOption(item) : undefined;
              const { subText, imageUrl, fallbackImageUrl } = getOptionCardData(item, selected);
              const isMissing = isOptionWithMissingSelection(item);
              const additionalText =
                isPresentationOnly &&
                isInStore &&
                (item.options ? item.options[0].appendCode : item.appendCode) &&
                selected
                  ? optionValues![selected.selectedOption].code
                  : undefined;

              return (
                <div
                  key={index}
                  className={getAdvancedItemClasses(item.isAdvanced, isAdvancedExpanded)}
                  ref={cardItemRef}
                >
                  <Card
                    card={item}
                    cardImages={[imageUrl]}
                    cardText={item.name}
                    isSelected={false}
                    tabIndex={index + 1}
                    cardClick={cardClick}
                    hideMobileInfoText={false}
                    cardSize={CardSize.Medium}
                    isParent={true}
                    cardOrientation={cardOrientation}
                    cardSubText={subText}
                    cardSubAdditionalText={additionalText}
                    isPresentationOnly={isPresentationOnly}
                    showWarning={isMissing}
                    cardFallbackImageUrl={fallbackImageUrl}
                    isGrid={isGrid}
                  />
                </div>
              );
            }
          }
        })}
      {!isGroup &&
        cardSubItems &&
        (cardSubItems as number[]).map((cardSubItem: number, index: number) => {
          const item: OptionValue = (cardItems as typeOptionValue)[cardSubItem];

          if (item && !isOptionValueHiddenByRule(item.id, hiddenOptionsAndValues, affectedConfigurationTypeId)) {
            let imageThumbnail;
            let fallbackImageThumbnail;

            if (item.imageUrlTemplate === '{fabricThumbnailDefaultPath}' && item.imageName === '{fabricImageName}') {
              imageThumbnail = imageUrlBuilder({
                imageUrls: [`${fabricThumbnailDefaultPath}${selectedFabric?.imageName}`],
                imageDefinition: imageDefinition.thumbnail,
                cdnDomain,
              }).src;
            } else {
              const imagePaths = getImagePaths(
                item.imageUrlTemplate,
                item.imageName,
                selectedFabric?.layerImageName || '',
                cdnDomain,
                true,
              );

              imageThumbnail = imagePaths.imageThumbnail;
              fallbackImageThumbnail = imagePaths.fallbackImageThumbnail;
            }

            return (
              <Card
                key={index}
                card={item}
                cardImages={[imageThumbnail]}
                cardText={item.name}
                isSelected={selectedItemId ? selectedItemId === item.id : false}
                tabIndex={index + 1}
                cardClick={cardClick}
                cardSize={CardSize.Medium}
                isParent={false}
                cardOrientation={cardOrientation}
                cardInfoIconType={CardInfoIconType.Light}
                cardSubText={getSubText(selectedOption, item)}
                showTooltip={viewSize === ViewSize.Mobile || viewSize === ViewSize.Tablet}
                cardPrice={
                  item.additionalPrice
                    ? '+' + formatPrice(item.additionalPrice.amount, item.additionalPrice.code)
                    : undefined
                }
                cardFallbackImageUrl={fallbackImageThumbnail}
                isGrid={isGrid}
              />
            );
          }
        })}
    </>
  );
}
